import React from 'react';
import { connect } from 'react-redux';
import { AccordionV1IsEmptyFn, withAuthorPanelSwitch, withStandardBaseCssClass, } from '@adobe/aem-core-components-react-spa';
import { ComponentMapping, Container } from '@adobe/aem-react-editable-components';
import { Rotate } from '@yme/assets/icons';
import { AccordionContainer, AccordionItemContainer, ButtonStyled, ContentStyled, LayoutStyled, SVGIconToggle, TitleStyled, } from '@yme/atoms/accordion';
import { AlwaysActiveMarkStyled } from '@yme/atoms/accordion/styled';
import { GridWrapper } from '@yme/atoms/grid';
import { getRTL } from '@yme/selectors/common';
import { colorBrandYamahaRed } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { INDENT } from '@yme/tokens/sizes';
import { HeadingLevel2 } from '@yme/tokens/typography';
import { IndentWrapper } from '../Separator';
import { CQ_KEY, DATA_ATTR_VALUE, DATA_CMP_IS, DATA_PANEL_CONTAINER, DEFAULT_ACCORDION_CLASS, } from './constants';
import { buildDisplayItemStyles, handleAccordionNavClick, hasItemExpanded } from './helpers';
class Accordion extends Container {
    constructor(props) {
        super(props);
        const { componentMapping, expandedItems } = this.props;
        this.state = {
            componentMapping: componentMapping || ComponentMapping,
            expandedItems,
        };
        this.handleAccordionNavClick = this.handleAccordionNavClick.bind(this);
    }
    get accordionContainerProps() {
        const attrs = this.containerProps;
        const { baseCssClass } = this.props;
        attrs.className = `${attrs.className} ${baseCssClass}`;
        attrs[DATA_CMP_IS] = DATA_ATTR_VALUE;
        attrs[DATA_PANEL_CONTAINER] = DATA_ATTR_VALUE;
        return attrs;
    }
    componentDidMount() {
        this.setState({
            expandedItems: [],
        });
    }
    componentDidUpdate(prevProps) {
        const { cqItemsOrder, activeIndexFromAuthorPanel } = this.props;
        if (activeIndexFromAuthorPanel !== undefined &&
            prevProps.activeIndexFromAuthorPanel !== activeIndexFromAuthorPanel) {
            this.setState({
                expandedItems: [cqItemsOrder[activeIndexFromAuthorPanel]],
            });
        }
    }
    handleAccordionNavClick(itemKey) {
        const { singleExpansion } = this.props;
        const { expandedItems } = handleAccordionNavClick({
            expandedItemsState: this.state.expandedItems,
            itemKey,
            singleExpansion,
        });
        this.setState({
            expandedItems,
        });
    }
    displayItem(key, isExpanded) {
        const { cqItemsOrder, baseCssClass, isInEditor } = this.props;
        const indexToShow = cqItemsOrder.indexOf(key);
        if (isInEditor || isExpanded) {
            const { cssClass } = buildDisplayItemStyles({
                isExpanded,
                baseCssClass,
            });
            return (React.createElement(ContentStyled, { isShowed: isExpanded, className: cssClass, role: "region" }, this.childComponents[indexToShow]));
        }
        return null;
    }
    renderHeadingButton(key, item, buttonCssClass) {
        const { baseCssClass, isRightToLeft, alwaysActiveItem, labels } = this.props;
        const isExpanded = hasItemExpanded({
            expandedItems: this.state.expandedItems,
            key,
        });
        const withAlwaysActiveMark = key === alwaysActiveItem;
        const { titleBaseCssClass, iconBaseCssClass } = buildDisplayItemStyles({
            isExpanded,
            baseCssClass,
        });
        const ButtonContent = this.props.isInCookieManagement ? (React.createElement(React.Fragment, null,
            React.createElement(FlexWrapper, null,
                React.createElement(SVGIconToggle, { className: iconBaseCssClass, type: Rotate }),
                React.createElement(TitleStyled, { className: titleBaseCssClass, isRightToLeft: isRightToLeft }, item[CQ_KEY])),
            withAlwaysActiveMark && (React.createElement(AlwaysActiveMarkStyled, { color: colorBrandYamahaRed }, labels?.cookies_alwaysActive_mark)))) : (React.createElement(React.Fragment, null,
            React.createElement(TitleStyled, { className: titleBaseCssClass, isRightToLeft: isRightToLeft }, item[CQ_KEY]),
            React.createElement(SVGIconToggle, { className: iconBaseCssClass, type: Rotate })));
        return (React.createElement(LayoutStyled, null,
            React.createElement(ButtonStyled, { type: "button", className: buttonCssClass, isShowed: isExpanded, onClick: () => {
                    this.handleAccordionNavClick(key);
                } }, ButtonContent)));
    }
    get accordionContent() {
        const { cqItemsOrder, baseCssClass, cqItems, headingElement } = this.props;
        return cqItemsOrder.map((key, index) => {
            const item = cqItems[key];
            const isExpanded = hasItemExpanded({
                expandedItems: this.state.expandedItems,
                key,
            });
            const { buttonCssClass, itemBaseCssClass, headerBaseCssClass } = buildDisplayItemStyles({
                isExpanded,
                baseCssClass,
            });
            return (React.createElement(AccordionItemContainer, { key: key, className: itemBaseCssClass, "data-cmp-index": index, "data-cmp-expanded": isExpanded },
                React.createElement(`${headingElement || 'h3'}`, {
                    className: headerBaseCssClass,
                }, this.renderHeadingButton(key, item, buttonCssClass)),
                this.displayItem(key, isExpanded)));
        });
    }
    renderAccordionContainer() {
        const { title, isRightToLeft, hasContainerPadding } = this.props;
        const isEmpty = AccordionV1IsEmptyFn(this.props);
        return (React.createElement(AccordionContainer, { isRightToLeft: isRightToLeft, hasContainerPadding: hasContainerPadding },
            title && (React.createElement(FlexWrapper, { mb: INDENT.sm, mbDesktop: INDENT.m },
                React.createElement(HeadingLevel2, null, title))),
            this.accordionContent,
            !isEmpty && this.placeholderComponent));
    }
    render() {
        const { isCustomGrid, isRemoveBottomIndent } = this.props;
        if (isCustomGrid) {
            return (React.createElement(IndentWrapper, { isRemoveBottomIndent: isRemoveBottomIndent },
                React.createElement("div", { ...this.accordionContainerProps },
                    React.createElement(GridWrapper, null, this.renderAccordionContainer()))));
        }
        return (React.createElement(IndentWrapper, { isRemoveBottomIndent: isRemoveBottomIndent },
            React.createElement("div", { ...this.accordionContainerProps }, this.renderAccordionContainer())));
    }
}
const mapStateToProps = (state) => ({
    isRightToLeft: getRTL(state),
});
export default connect(mapStateToProps, null)(withStandardBaseCssClass(withAuthorPanelSwitch(Accordion), DEFAULT_ACCORDION_CLASS));
